/**
 * DetailTable - Flat paginated table for well detail data
 */

import React, {
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Table,
  TableBody,
  TablePagination,
  Paper,
  Typography,
} from "@material-ui/core";
import { useColumnFilters, usePagination } from "../../../hooks";
import { createEmptyTotals, accumulateWellDetailRow } from "../../../builders";
import {
  computePctPermit,
  calcRemainingGal,
  calcRemainingAf,
  formatGallons,
  formatPermit,
  formatAcreFeet,
} from "../../../utils";
import { COLUMN_MODES } from "../../../constants";
import { exportDetailTable } from "../../../exports";
import { PercentBar, StatusCell } from "../../atoms";
import {
  SummaryTableContainer,
  StyledTableCell,
  StickyFirstCell,
  DataRow,
  TotalsRow,
  NoDataMessage,
} from "../../styled";
import DetailHeader from "./DetailHeader";

/**
 * Detail table component
 * @param {Object} props
 * @param {Array} props.rows - Data rows
 * @param {string} props.columnMode - Column display mode
 * @param {React.Ref} ref - Ref for imperative handle
 */
const DetailTable = forwardRef(({ rows, columnMode }, ref) => {
  const {
    columnFilters,
    filterOptions,
    orderBy,
    order,
    handleFilterChange,
    handleSort,
    filteredAndSortedRows,
  } = useColumnFilters(rows, "aquifer_name", "asc");

  const {
    page,
    perPage,
    handleChangePage,
    handleChangePerPage,
    paginatedItems: paginatedRows,
  } = usePagination(filteredAndSortedRows, 50);

  // Calculate totals from filtered rows
  // Production values are summed from every row.
  // Permit values are only counted once per aggregate system + year to avoid
  // misleading totals (multiple wells can share the same system permit).
  const totals = useMemo(() => {
    if (filteredAndSortedRows.length === 0) return createEmptyTotals();
    const t = createEmptyTotals();
    const seenSystems = new Set();
    for (const row of filteredAndSortedRows) {
      accumulateWellDetailRow(t, row, seenSystems);
    }
    return t;
  }, [filteredAndSortedRows]);

  const totalRemainingGal = calcRemainingGal(totals);
  const totalRemainingAf = calcRemainingAf(totals);
  const totalPctPermit = computePctPermit(totals);
  const isTotalNegative = totalRemainingGal < 0;

  const handleExport = useCallback(() => {
    exportDetailTable(filteredAndSortedRows, totals, columnMode);
  }, [filteredAndSortedRows, totals, columnMode]);

  // Expose export function and row count to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      exportCsv: handleExport,
      rowCount: filteredAndSortedRows.length,
    }),
    [handleExport, filteredAndSortedRows.length]
  );

  if (!rows || rows.length === 0) {
    return (
      <NoDataMessage>
        <Typography variant="body1">No data for selected filters.</Typography>
      </NoDataMessage>
    );
  }

  return (
    <>
      <SummaryTableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <DetailHeader
            columnMode={columnMode}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            columnFilters={columnFilters}
            filterOptions={filterOptions}
            onFilterChange={handleFilterChange}
          />
          <TableBody>
            {paginatedRows.map((row) => {
              const remainingGal = calcRemainingGal(row);
              const remainingAf = calcRemainingAf(row);
              const isNegative = remainingGal < 0;
              const pctPermit = computePctPermit(row);

              return (
                <DataRow key={`${row.well_ndx}-${row.report_year}`}>
                  <StickyFirstCell>{row.cuwcd_well_number}</StickyFirstCell>
                  <StyledTableCell>{row.state_well_number}</StyledTableCell>
                  <StyledTableCell>{row.well_name}</StyledTableCell>
                  <StyledTableCell>{row.agg_system_name}</StyledTableCell>
                  <StyledTableCell>{row.report_year}</StyledTableCell>
                  <StyledTableCell>{row.aquifer_name}</StyledTableCell>
                  <StyledTableCell align="right">
                    {formatPermit(row.h_permit_af)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatPermit(row.o_permit_af)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatPermit(row.total_permit_af)}
                  </StyledTableCell>
                  {columnMode === COLUMN_MODES.full && (
                    <>
                      <StyledTableCell align="right">
                        {formatGallons(row.jan)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.feb)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.mar)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.apr)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.may)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.jun)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.jul)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.aug)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.sep)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.oct)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.nov)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {formatGallons(row.dec)}
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell align="right">
                    {formatGallons(row.ytd_gal)}
                  </StyledTableCell>
                  <StatusCell negative={isNegative}>
                    {formatGallons(remainingGal)}
                  </StatusCell>
                  <StyledTableCell align="right">
                    {formatAcreFeet(row.ytd_af)}
                  </StyledTableCell>
                  <StatusCell negative={isNegative}>
                    {formatAcreFeet(remainingAf)}
                  </StatusCell>
                  <PercentBar value={pctPermit} />
                </DataRow>
              );
            })}
            {/* Totals row - permits are deduplicated by system+year */}
            <TotalsRow>
              <StickyFirstCell>
                <strong>Totals</strong>
              </StickyFirstCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell align="right">
                {formatPermit(totals.h_permit_af)}
              </StyledTableCell>
              <StyledTableCell align="right">
                {formatPermit(totals.o_permit_af)}
              </StyledTableCell>
              <StyledTableCell align="right">
                {formatPermit(totals.total_permit_af)}
              </StyledTableCell>
              {columnMode === COLUMN_MODES.full && (
                <>
                  <StyledTableCell align="right">
                    {formatGallons(totals.jan)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.feb)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.mar)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.apr)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.may)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.jun)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.jul)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.aug)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.sep)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.oct)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.nov)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(totals.dec)}
                  </StyledTableCell>
                </>
              )}
              <StyledTableCell align="right">
                {formatGallons(totals.ytd_gal)}
              </StyledTableCell>
              <StatusCell negative={isTotalNegative}>
                {formatGallons(totalRemainingGal)}
              </StatusCell>
              <StyledTableCell align="right">
                {formatAcreFeet(totals.ytd_af)}
              </StyledTableCell>
              <StatusCell negative={isTotalNegative}>
                {formatAcreFeet(totalRemainingAf)}
              </StatusCell>
              <PercentBar value={totalPctPermit} />
            </TotalsRow>
          </TableBody>
        </Table>
      </SummaryTableContainer>
      <TablePagination
        component="div"
        count={filteredAndSortedRows.length}
        page={page}
        onChangePage={handleChangePage}
        rowsPerPage={perPage}
        onChangeRowsPerPage={handleChangePerPage}
        rowsPerPageOptions={[25, 50, 100, 250]}
      />
    </>
  );
});

export default DetailTable;
